import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import DynamicSuspenseContainer from 'src/components/DynamicSuspenseContainer/DynamicSuspenseContainer'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import PopupMenu from 'src/components/PopupMenu/PopupMenu'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgRecurring from 'src/components/Svgs/SvgRecurring'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import UpdateTransactionStatusPopupMenu from 'src/components/UpdateTransactionStatusPopupMenu/UpdateTransactionStatusPopupMenu'
import PluralTrns from 'src/localization/PluralTrns'
import Trns, { useTranslate } from 'src/localization/Trns'
import { TransactionListModel } from 'src/models/transaction.models'
import { updateBulkTransactions } from 'src/navigation/urls'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { useSuspenseQueryTransactionsCount } from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import {
  selectIsSelectAllTransactionsFromState,
  selectSelectAllTransactionsFiltersFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface TransactionsTableBulkFloatingToolbarProps {
  context: TransactionReportType
  filters?: TransactionsFiltersBody
  selectedTransactions: TransactionListModel[]
  showSyncWithIntegration: boolean
  orgExpenseReportId?: string
  handleCloseToolbar: () => void
  handleSync: () => void
}

const AllTransactionsSelectedCount: React.FC<{
  source: string
  filters: TransactionsFiltersBody
  expressions?: Record<string, string>
}> = ({ source, filters, expressions }) => {
  const { data: allTransactionsCount } = useSuspenseQueryTransactionsCount({
    filters,
  })

  return (
    <Trns
      source={source}
      expressions={{
        count: `${allTransactionsCount}`,
        ...expressions,
      }}
    />
  )
}

const TransactionsTableBulkFloatingToolbar: React.ForwardRefRenderFunction<
  HTMLDivElement,
  TransactionsTableBulkFloatingToolbarProps
> = (props, ref) => {
  const translate = useTranslate()
  const brandColorCta = useSelector(selectBrandColorCta)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const selectAllTransactionsFiltersBody =
    useSelector(selectSelectAllTransactionsFiltersFromState) || {}

  const { data: user } = useQueryOwnUser({})
  const { data: mandatoryExpenseDetails } = useQueryExpenseCategories({
    filters: {
      active: true,
      requiredForTransactions: true,
    },
  })
  const { data: expenseDetails } = useQueryExpenseCategories({ filters: {} })

  const selectedTransactionsCount = props.selectedTransactions.length
  const selectedTransactionsCanSyncToERPCount =
    props.selectedTransactions.filter((txn) =>
      txn.canPushToConnectedPlatform(
        activeIntegration?.connectedPlatform,
        mandatoryExpenseDetails,
      ),
    ).length

  const selectedEditableTransactionsCount = props.selectedTransactions.filter(
    (txn) =>
      txn.notPushedToConnectedPlatform(activeIntegration?.connectedPlatform) &&
      !txn.isSplit,
  ).length

  const hasExpenseDetails =
    expenseDetails !== undefined && expenseDetails.length > 0
  const activeIntegrationPlatformName = activeIntegration?.platformName
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  return (
    <FloatingToolbar
      visible={selectedTransactionsCount > 0 || allSelected}
      ref={ref}
      css={css`
        > div {
          column-gap: 20px;
          align-items: stretch;
        }
      `}
    >
      <span
        css={css`
          align-items: center;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          white-space: nowrap;
          display: inline-flex;
        `}
      >
        {!allSelected ? (
          <PluralTrns
            pluralSource={'${count} transactions selected'}
            singleSource={'1 transaction selected'}
            count={selectedTransactionsCount}
          />
        ) : (
          <DynamicSuspenseContainer
            fallback={<LoadingShimmer width={'200px'} height={'24px'} />}
          >
            <AllTransactionsSelectedCount
              source={'${count} transactions selected'}
              filters={selectAllTransactionsFiltersBody}
            />
          </DynamicSuspenseContainer>
        )}
      </span>
      {hasExpenseDetails &&
        (allSelected || selectedEditableTransactionsCount > 0) && (
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
              padding-top: 20px;
              padding-bottom: 20px;
              padding-left: 20px;
              border-left: 1px solid ${styleConstants.colors.gray_medium};
            `}
            regular={true}
            size={HoverableLinkSizes.Large}
            to={updateBulkTransactions()}
            ariaLabelSource={'Update'}
            testId={'updateBtn'}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                column-gap: 4px;
              `}
            >
              {allSelected ? (
                <Trns source={'Update'} />
              ) : (
                <Trns
                  source={'Update (${count})'}
                  expressions={{
                    count: `${selectedEditableTransactionsCount}`,
                  }}
                />
              )}
            </span>
          </HoverableLink>
        )}
      {(props.orgExpenseReportId !== undefined ||
        props.context === TransactionReportType.AccountingTransactions) && (
        <div
          css={css`
            padding-left: 20px;
            border-left: 1px solid ${styleConstants.colors.gray_medium};
            & > div {
              display: flex;
              flex-direction: column;
            }
          `}
        >
          <PopupMenu
            bottomOffset={10}
            leftOffset={-20}
            width={200}
            baseComponent={
              <HoverableLink
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: -4px;
                  padding-top: 20px;
                  padding-bottom: 20px;
                `}
                regular={true}
                size={HoverableLinkSizes.Large}
                ariaLabelSource={'Status'}
                testId={'statusBtn'}
              >
                <div
                  css={`
                    margin-right: 5px;
                  `}
                >
                  <Trns source={'Status'} />
                </div>
                <SvgChevronDown />
              </HoverableLink>
            }
            baseComponentDropdownOpen={
              <HoverableLink
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: -4px;
                  padding-top: 20px;
                  padding-bottom: 20px;
                `}
                regular={true}
                size={HoverableLinkSizes.Large}
                ariaLabelSource={'Status'}
                testId={'statusBtn'}
              >
                {/* on hover styling is not applied to these due to the PopupMenu being on top, so must style here */}
                <div
                  css={`
                    margin-right: 5px;
                    ${getFontCss(
                      16,
                      themeConstants.colors.cta_dark,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  <Trns source={'Status'} />
                </div>
                <SvgChevronUp fill={brandColorCta} />
              </HoverableLink>
            }
            menuComponent={
              <UpdateTransactionStatusPopupMenu
                transactions={props.selectedTransactions}
                orgExpenseReportId={props.orgExpenseReportId}
                filters={props.filters}
              />
            }
          />
        </div>
      )}
      {user?.hasOrgBookkeeperPermissions &&
        (allSelected || selectedTransactionsCanSyncToERPCount > 0) &&
        activeIntegrationPlatformName !== undefined &&
        props.showSyncWithIntegration && (
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
              padding-top: 20px;
              padding-bottom: 20px;
              padding-left: 20px;
              border-left: 1px solid ${styleConstants.colors.gray_medium};
            `}
            regular={true}
            size={HoverableLinkSizes.Large}
            onClick={props.handleSync}
            ariaLabel={translate('Sync with ${orgLvlIntegration}', {
              orgLvlIntegration: activeIntegrationPlatformName,
            })}
            testId={'syncWithIntegration'}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                column-gap: 4px;
              `}
            >
              <SvgRecurring
                fill={brandColorCta}
                css={css`
                  transform: scale(calc(19 / 24));
                  float: left;
                `}
              />
              {allSelected ? (
                <Trns
                  source={'Sync with ${orgLvlIntegration}'}
                  expressions={{
                    orgLvlIntegration: activeIntegrationPlatformName,
                  }}
                />
              ) : (
                <Trns
                  source={'Sync with ${orgLvlIntegration} (${count})'}
                  expressions={{
                    orgLvlIntegration: activeIntegrationPlatformName,
                    count: `${selectedTransactionsCanSyncToERPCount}`,
                  }}
                />
              )}
            </span>
          </HoverableLink>
        )}
      <div
        css={css`
          display: flex;
          align-items: center;
          padding-left: 20px;
          border-left: 1px solid ${styleConstants.colors.gray_medium};
        `}
      >
        <HoverableLink
          ariaLabelSource={'Close'}
          onClick={props.handleCloseToolbar}
          testId={'closeBtn'}
        >
          <div
            css={css`
              svg path {
                stroke: ${themeConstants.colors.font_primary};
              }
              &:hover {
                svg path {
                  stroke: ${themeConstants.colors.cta_dark};
                }
              }
              height: 24px;
            `}
          >
            <SvgClose />
          </div>
        </HoverableLink>
      </div>
    </FloatingToolbar>
  )
}

export default React.forwardRef(TransactionsTableBulkFloatingToolbar)
